// T-26 — Karte, die nach einer festen Verzögerung (Standard 2 s) neben einem
// Anker-Element erscheint.
//
// Warum kein Fluent-`Tooltip` und kein `title`-Attribut:
//  - Die Kontaktsuche ist bewusst Fluent-frei (B2-Regressionsschutz in
//    `KontaktSuchfeld.test.tsx`).
//  - Die Verzögerung eines nativen `title` legt der Browser fest (Edge ca.
//    0,5–1 s), und es zeigt nur eine Textzeile — gewünscht sind 2 s und alle
//    Angaben eines Kontakts.
//
// Die Karte rendert per Portal in `document.body` mit `position: fixed`. In der
// Trefferliste (`overflow: auto`, max. 260 px) oder im WindowManager-Fenster
// würde eine absolut positionierte Karte sonst abgeschnitten. Die Position wird
// aus dem Rechteck des Ankers berechnet und ins Fenster geklemmt: rechts
// daneben, sonst links, sonst darunter, sonst darüber — nie aus dem Fenster.
//
// `pointer-events: none`: Die Karte ist reine Information und darf weder die
// Auswahl per `onMouseDown` noch das Hover der Zeile darunter stören.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/** Standard-Verzögerung in ms (PO: „nach 2 Sekunden"). */
export const HOVER_KARTE_VERZOEGERUNG_MS = 2000;

/** Abstand zum Anker und Mindestabstand zum Fensterrand in px. */
const ABSTAND = 8;

const useStyles = makeStyles({
    karte: {
        position: "fixed",
        zIndex: 100010,
        boxSizing: "border-box",
        maxWidth: `min(360px, calc(100vw - ${2 * ABSTAND}px))`,
        maxHeight: `calc(100vh - ${2 * ABSTAND}px)`,
        overflowY: "auto",
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("10px"),
        ...shorthands.padding("10px", "12px"),
        backgroundColor: REFINED.white,
        boxShadow: REFINED.cardShadow,
        fontFamily: REFINED.fontUI,
        fontSize: "0.85rem",
        color: REFINED.ink,
        pointerEvents: "none",
    },
});

export type KartenPosition = { left: number; top: number };

/**
 * Position der Karte fürs Fenster berechnen — rein, damit testbar.
 * Reihenfolge: rechts neben dem Anker, links daneben, darunter, darüber. Das
 * Ergebnis wird immer ins Fenster geklemmt (Mindestabstand {@link ABSTAND}).
 */
export function kartenPosition(
    anker: { left: number; top: number; right: number; bottom: number },
    karte: { width: number; height: number },
    fenster: { width: number; height: number },
): KartenPosition {
    const passtX = (l: number) => l >= ABSTAND && l + karte.width <= fenster.width - ABSTAND;
    const passtY = (t: number) => t >= ABSTAND && t + karte.height <= fenster.height - ABSTAND;
    const klemm = (v: number, max: number) => Math.max(ABSTAND, Math.min(v, max - ABSTAND));

    let left: number;
    let top: number;
    if (passtX(anker.right + ABSTAND)) {
        left = anker.right + ABSTAND;
        top = anker.top;
    } else if (passtX(anker.left - ABSTAND - karte.width)) {
        left = anker.left - ABSTAND - karte.width;
        top = anker.top;
    } else {
        left = anker.left;
        const unten = anker.bottom + ABSTAND / 2;
        const oben = anker.top - ABSTAND / 2 - karte.height;
        top = passtY(unten) || !passtY(oben) ? unten : oben;
    }
    return {
        left: klemm(left, fenster.width - karte.width),
        top: klemm(top, fenster.height - karte.height),
    };
}

export function VerzoegerteHoverKarte(props: {
    /** Element, neben dem die Karte erscheint. `null` = keine Karte. */
    anker: HTMLElement | null;
    /** Soll die Karte (nach Ablauf der Verzögerung) sichtbar sein? */
    offen: boolean;
    /** Verzögerung in ms, Standard {@link HOVER_KARTE_VERZOEGERUNG_MS}. */
    delayMs?: number;
    /** DOM-Id der Karte — für `aria-describedby` am Anker. */
    id?: string;
    children: React.ReactNode;
}): JSX.Element | null {
    const s = useStyles();
    const { anker, offen } = props;
    const delay = props.delayMs ?? HOVER_KARTE_VERZOEGERUNG_MS;
    const [sichtbar, setSichtbar] = React.useState(false);
    const [pos, setPos] = React.useState<KartenPosition | null>(null);
    const karteRef = React.useRef<HTMLDivElement | null>(null);

    // Timer: startet neu, sobald sich Anker oder Offen-Zustand ändern; wird
    // geschlossen, verschwindet die Karte sofort.
    React.useEffect(() => {
        setSichtbar(false);
        setPos(null);
        if (!offen || !anker) return;
        const handle = window.setTimeout(() => setSichtbar(true), delay);
        return () => window.clearTimeout(handle);
    }, [offen, anker, delay]);

    const positionieren = React.useCallback(() => {
        const el = karteRef.current;
        if (!el || !anker) return;
        const a = anker.getBoundingClientRect();
        const k = el.getBoundingClientRect();
        const neu = kartenPosition(
            { left: a.left, top: a.top, right: a.right, bottom: a.bottom },
            { width: k.width, height: k.height },
            { width: aktivesFenster().innerWidth, height: aktivesFenster().innerHeight },
        );
        // Nur bei echter Änderung setzen — sonst Render-Schleife.
        setPos((alt) => (alt && alt.left === neu.left && alt.top === neu.top ? alt : neu));
    }, [anker]);

    // Erst messen (Karte unsichtbar gerendert), dann setzen — vor dem Paint.
    React.useLayoutEffect(() => {
        if (sichtbar) positionieren();
    }, [sichtbar, positionieren]);

    // Scrollt die Liste oder ändert sich das Fenster, wandert die Karte mit.
    React.useEffect(() => {
        if (!sichtbar) return;
        alleFenster.addEventListener("scroll", positionieren, true);
        alleFenster.addEventListener("resize", positionieren);
        return () => {
            alleFenster.removeEventListener("scroll", positionieren, true);
            alleFenster.removeEventListener("resize", positionieren);
        };
    }, [sichtbar, positionieren]);

    if (!sichtbar || !anker || typeof document === "undefined") return null;
    return createPortal(
        <div
            ref={karteRef}
            id={props.id}
            role="tooltip"
            className={s.karte}
            style={
                pos
                    ? { left: pos.left, top: pos.top }
                    : // Messdurchlauf: schon im Layout, aber noch nicht zu sehen.
                      { left: 0, top: 0, visibility: "hidden" }
            }
        >
            {props.children}
        </div>,
        document.body,
    );
}

export default VerzoegerteHoverKarte;
